import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  MapPin, Calendar, ArrowRight, Shield } from 'lucide-react';

const PennState: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Penn State";

    return (
        <div className="min-h-screen bg-white font-sans text-[#001E44] selection:bg-[#001E44] selection:text-white">
            {/* Top Bar - Dark Blue */}
            <div className="bg-[#001E44] text-white py-1.5 px-6 flex justify-between items-center text-xs font-bold">
                <div className="hidden md:flex gap-4">
                    <a href="#" className="hover:underline opacity-80 hover:opacity-100">Faculty/Staff</a>
                    <a href="#" className="hover:underline opacity-80 hover:opacity-100">Alumni</a>
                    <a href="#" className="hover:underline opacity-80 hover:opacity-100">Current Students</a>
                </div>
                <div className="flex gap-4 ml-auto">
                    <a href="#" className="hover:underline opacity-80 hover:opacity-100">Directory</a>
                    <a href="#" className="hover:underline opacity-80 hover:opacity-100">Maps</a>
                    <a href="#" className="hover:underline opacity-80 hover:opacity-100">Athletics</a>
                </div>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 shadow-sm sticky top-0 z-50">
                <div className="max-w-[1240px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                         <div className="bg-[#001E44] text-white p-1 rounded-full">
                             {/* Nittany Lion Shield Implied */}
                             <Shield size={32} />
                         </div>
                        <span className="font-serif text-2xl font-bold text-[#001E44] tracking-tight">{schoolName}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#001E44] text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#3EA3DD] transition-colors relative group">
                            Academics
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#001E44] transition-all group-hover:w-full"></span>
                        </a>
                        <a href="#" className="hover:text-[#3EA3DD] transition-colors relative group">
                            Admissions
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#001E44] transition-all group-hover:w-full"></span>
                        </a>
                        <a href="#" className="hover:text-[#3EA3DD] transition-colors relative group">
                            Research
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#001E44] transition-all group-hover:w-full"></span>
                        </a>
                        <a href="#" className="hover:text-[#3EA3DD] transition-colors relative group">
                            Campuses
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#001E44] transition-all group-hover:w-full"></span>
                        </a>
                         <a href="#" className="hover:text-[#3EA3DD] transition-colors relative group">
                            News
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#001E44] transition-all group-hover:w-full"></span>
                        </a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <Search className="cursor-pointer text-[#001E44] hover:text-[#3EA3DD]" size={22} />
                         <Menu className="cursor-pointer lg:hidden text-[#001E44]" size={26} />
                    </div>
                </div>
            </nav>

            {/* Hero - Old Main */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1599580633851-40455ca37367?q=80&w=2070&auto=format&fit=crop" 
                    alt="Old Main" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 {/* Blue Wash */}
                <div className="absolute inset-0 bg-[#001E44]/40 mix-blend-multiply"></div>

                <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-6">
                     <div className="max-w-4xl">
                         <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 drop-shadow-xl">
                             We Are <br/>
                             <span className="text-[#3EA3DD]">{schoolName}.</span>
                         </h1>
                         <p className="text-xl md:text-2xl mb-10 font-medium text-gray-100 max-w-2xl mx-auto drop-shadow-md">
                             Inspiring achievement and creating a global community of innovators.
                         </p>
                         <Button className="bg-[#001E44] hover:bg-[#001533] text-white font-bold uppercase tracking-widest px-10 h-14 rounded-full border-2 border-white/50 hover:border-white transition-all shadow-lg">
                             Visit Us
                         </Button>
                     </div>
                </div>
            </header>

            {/* Stats / Impact */}
            <section className="py-24 px-6 md:px-12 bg-[#f4f7f9]">
                <div className="max-w-[1240px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                     <div>
                         <h3 className="text-5xl font-black text-[#001E44] mb-2">Top 1%</h3>
                         <p className="text-gray-600 font-bold uppercase text-sm tracking-wide">In World University Rankings</p>
                     </div>
                      <div>
                         <h3 className="text-5xl font-black text-[#001E44] mb-2">$1B+</h3>
                         <p className="text-gray-600 font-bold uppercase text-sm tracking-wide">Annual Research Expenditures</p>
                     </div>
                      <div>
                         <h3 className="text-5xl font-black text-[#001E44] mb-2">24</h3>
                         <p className="text-gray-600 font-bold uppercase text-sm tracking-wide">Campuses in Pennsylvania</p>
                     </div>
                </div>
            </section>

             {/* News Blocks */}
             <section className="bg-white py-24 px-6 md:px-12">
                 <div className="max-w-[1240px] mx-auto">
                     <div className="flex justify-between items-end mb-12 border-b-2 border-[#001E44] pb-4">
                         <h2 className="text-3xl font-bold text-[#001E44]">{schoolName} News</h2>
                         <a href="#" className="font-bold text-[#001E44] uppercase text-xs hover:text-[#3EA3DD] mb-2">All News</a>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                         {[
                             `${schoolName} Health announces new cancer center`,
                             "Research team discovers new species of dinosaur",
                             "Smeal College of Business ranked #1 for Supply Chain",
                             "Nittany Lions win national championship in wrestling"
                         ].map((title, i) => (
                             <div key={i} className="group cursor-pointer">
                                 <div className="h-48 bg-gray-200 mb-4 rounded-lg overflow-hidden relative">
                                     <img src={`https://source.unsplash.com/random/400x300?sig=${i+20}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="News" />
                                     <div className="absolute inset-0 bg-[#001E44]/0 group-hover:bg-[#001E44]/20 transition-colors"></div>
                                 </div>
                                 <h3 className="text-lg font-bold text-[#001E44] group-hover:text-[#3EA3DD] transition-colors mb-2 leading-snug">{title}</h3>
                                 <div className="flex items-center gap-1 text-[#001E44] text-sm font-bold mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                     Read More <ArrowRight size={14}/>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#001E44] text-white py-16 px-6 md:px-12">
                  <div className="max-w-[1240px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                           <div className="flex items-center gap-3 mb-6">
                                <Shield size={32} />
                                <span className="font-serif text-2xl font-bold tracking-tight">{schoolName}</span>
                           </div>
                          <p className="text-gray-300 mb-6">
                              201 Old Main <br/>
                              University Park, PA 16802 <br/>
                              (814) 865-4700
                          </p>
                      </div>

                      <div className="grid grid-cols-2 gap-12 font-bold text-gray-300">
                          <ul className="space-y-3 hover:[&_a]:text-white">
                              <li><a href="#">Privacy</a></li>
                              <li><a href="#">Non-discrimination</a></li>
                              <li><a href="#">Equal Opportunity</a></li>
                              <li><a href="#">Accessibility</a></li>
                          </ul>
                          <ul className="space-y-3 hover:[&_a]:text-white">
                               <li><a href="#">Copyright</a></li>
                              <li><a href="#">Contact Us</a></li>
                              <li><a href="#">Hotlines</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1240px] mx-auto mt-16 text-center text-xs text-gray-500 border-t border-white/10 pt-8">
                      © 2026 {schoolName}
                  </div>
             </footer>
        </div>
    );
};

export default PennState;
